import {
  Checkbox,
  ConfirmationDialogBox,
  DatePicker,
  Grid,
  IconButton,
  TableCell,
  TableRow,
  TextField,
  Typography,
} from '@atgfr8/atg-ui-tailwind';
import {
  refundAdjustmentError,
  retractionAdjustmentError,
} from '@common/order/helperFunctions';
import {
  BasicTable,
  SelectInput,
  TableRowErrors,
  ToastType,
} from '@components';
import { Column } from '@components/BasicTable/BasicTable';
import DisabledTextField from '@components/TextField/DisabledTextField';
import {
  checkIfDialogBoxIsOpen,
  closeDialogBox,
  openDialogBox,
} from '@store/dialogBox';
import {
  orderStore,
  updateOrderState,
  validateFieldForOrder,
} from '@store/orders';
import { PlusCircleOutlineIcon, DeleteIcon } from '@atgfr8/icons';
import { IAdjustment } from '@typeDefinitions/adjustmentTypes';
import { formatDataTestId } from '@utils/dataTestIdFormatter';
import {
  formatAmount,
  handleToast,
  isAdmin,
  isSuperAdmin,
  orderTableErrors,
} from '@utils/utils';
import {
  adjustmentsOptions,
  DELETE_ADJUSTMENTS_LINE_ITEM_DIALOG_ID,
} from '@views/order/constants';
import { Accessor, createSignal, Index, Show } from 'solid-js';

import { removeTableRow } from '../../carrier/stops/stopsEditor/utilFunctions';
import classes from './classes';
import { AdjustmentErrors } from './components';

export const Adjustment = () => {
  const [deleteIndex, setDeleteIndex] = createSignal<number | null>(null);

  const columns: Column<IAdjustment>[] = [
    {
      name: 'Description',
    },
    {
      name: 'Delinquent',
    },
    {
      name: 'Settled Date',
    },
    {
      name: 'Amount',
    },
    {
      name: '',
    },
  ];

  const addRow = () => {
    const adjustments = [...orderStore.order.adjustments];
    adjustments.push({
      operationType: 'Insert',
      id: 0,
      orderId: orderStore.order.id,
      amount: 0,
      description: '',
      settledDate: null,
      retraction: null,
      order: null,
    });
    updateOrderState({ adjustments: adjustments });
  };

  const onAdjustmentChange = (
    key: string,
    value: string | number,
    index: number,
  ) => {
    const adjustments = [...orderStore.order.adjustments];
    if (adjustments[index].operationType === 'None') {
      adjustments[index] = {
        ...adjustments[index],
        [key]: value,
        operationType: 'Update',
      };
    } else {
      adjustments[index] = { ...adjustments[index], [key]: value };
    }
    updateOrderState({ adjustments: adjustments });
  };

  const footerRow = [
    {
      renderCell: () => (
        <IconButton
          onClick={addRow}
          disabled={orderStore.isReadOnly}
          dataTestId="add-adjustment-button"
        >
          <PlusCircleOutlineIcon class={classes.addIcon} size={20} />
        </IconButton>
      ),
      key: 'ideaTitle',
    },
    {
      key: '#',
    },
    {
      renderCell: () => (
        <Typography variant="body2" class={classes.totalLabel}>
          Total
        </Typography>
      ),
      key: '#',
    },
    {
      renderCell: () => (
        <Typography variant="body2" class={classes.totalAmount}>
          {formatAmount(
            orderStore.order.adjustments.reduce(
              (acc, curr) => Number(acc) + Number(curr.amount),
              0,
            ),
          )}
        </Typography>
      ),
      key: 'uploadDoc',
    },
    {
      key: '#',
    },
  ];

  const cellStyles = {
    padding: '1px 1px',
    'border-bottom': '0px !important',
  };

  const allowToSelectRetractionOrRefund = (value: number) => {
    const adjustments = orderStore.order.adjustments.filter(
      (x) => x.operationType != 'Delete',
    );
    const selectedElementCount = adjustments.filter(
      (x) => x.retraction == value,
    ).length;

    switch (value) {
      case 1: // 90 Days retraction
        // check to see if 90 days retraction is selected more than 1 time
        const count3 = adjustments.filter((x) => x.retraction === 3).length;
        if (retractionAdjustmentError(selectedElementCount, count3)) {
          handleToast(
            ToastType.Error,
            '90 Days Retraction can not be performed as action already exists and no corresponding refund is created for it.',
          );
        }
        return;
      case 2: // 120 Days retraction
        // check to see if 120 days retraction is selected more than 1 time
        // get the refund count for 120 day refund
        const count2 = adjustments.filter((x) => x.retraction === 5).length;
        if (retractionAdjustmentError(selectedElementCount, count2))
          if (selectedElementCount > 1) {
            handleToast(
              ToastType.Error,
              '120 Days Retraction can not be performed as action already exists and no corresponding refund is created for it.',
            );
          }
        return;
      case 3: // 90 Days refund
        // check to see if  1 90 day retraction is there
        const count = adjustments.filter((x) => x.retraction === 1).length;
        if (refundAdjustmentError(count, selectedElementCount)) {
          handleToast(
            ToastType.Error,
            '90 Days Refund can not be performed as corresponding retraction is not present.',
          );
        }
        return;
      case 5: // 120 Days refund
        // check to see if  1 120 day retraction is there
        const count1 = adjustments.filter((x) => x.retraction === 2).length;
        if (refundAdjustmentError(count1, selectedElementCount)) {
          handleToast(
            ToastType.Error,
            '120 Days Refund can not be performed as corresponding retraction is not present.',
          );
        }
    }
  };

  const canAdjust = () => {
    if (isAdmin() || isSuperAdmin()) return true;
    return false;
  };

  return (
    <Grid container>
      <AdjustmentErrors />
      <Grid item size={12}>
        <BasicTable
          footerRow={isSuperAdmin() ? footerRow : undefined}
          cellClasses="px-px! py-px!"
          columns={columns}
          children={
            <Index each={orderStore.order.adjustments}>
              {(item: Accessor<IAdjustment>, index) => (
                <Show when={item().operationType !== 'Delete'}>
                  <TableRow class={classes.tableRow}>
                    <TableCell style={cellStyles}>
                      <TextField
                        value={item().description}
                        onChange={(value: string) => {
                          onAdjustmentChange('description', value, index);
                        }}
                        class={'bg-white rounded-md'}
                        label=""
                        placeholder="Description"
                        error={Boolean(
                          orderStore.orderFormError &&
                          orderStore.orderFormError[
                            `adjustments[${index}].description`
                          ],
                        )}
                        on:blur={() =>
                          validateFieldForOrder(
                            `adjustments.${index}.description`,
                            `adjustments[${index}].description`,
                          )
                        }
                        disabled={
                          !canAdjust() ||
                          item().settledDate !== null ||
                          orderStore.isReadOnly
                        }
                        size="sm"
                        dataTestId={`description-textInput-${index}`}
                      />
                    </TableCell>
                    <TableCell style={{ ...cellStyles, 'min-width': '80px' }}>
                      <SelectInput
                        menuItems={adjustmentsOptions.map((o) => ({
                          label: o.label,
                          value: String(o.value),
                        }))}
                        label=""
                        value={
                          item().retraction != null
                            ? String(item().retraction)
                            : ''
                        }
                        onChange={(val) => {
                          onAdjustmentChange('retraction', val, index);
                          allowToSelectRetractionOrRefund(Number(val));
                        }}
                        disabled={
                          !canAdjust() ||
                          item().settledDate !== null ||
                          orderStore.isReadOnly
                        }
                        size="sm"
                        dataTestId={formatDataTestId(
                          'Adjustment retraction',
                          'selectInput',
                        )}
                      />
                    </TableCell>
                    <TableCell style={cellStyles}>
                      <DisabledTextField
                        value={item().settledDate?.toString() ?? ''}
                        size="sm"
                      />
                    </TableCell>
                    <TableCell
                      style={{
                        ...cellStyles,
                        'min-width': '90px',
                        'max-width': '90px',
                      }}
                    >
                      <TextField
                        label=""
                        type="number"
                        placeholder="Amount"
                        value={item().amount || ''}
                        onChange={async (value: unknown) => {
                          onAdjustmentChange('amount', value as number, index);
                          await validateFieldForOrder(
                            `adjustments[${index}].amount`,
                            'adjustments[' + index + '].amount',
                          );
                        }}
                        class={'bg-white rounded-md'}
                        error={Boolean(
                          orderStore.orderFormError &&
                          orderStore.orderFormError[
                            `adjustments[${index}].amount`
                          ],
                        )}
                        on:blur={() =>
                          validateFieldForOrder(
                            `adjustments.${index}.amount`,
                            `adjustments[${index}].amount`,
                          )
                        }
                        disabled={
                          !canAdjust() ||
                          item().settledDate !== null ||
                          orderStore.isReadOnly
                        }
                        size="sm"
                        dataTestId={`amount-textInput-${index}`}
                      />
                    </TableCell>
                    <TableCell
                      style={{ padding: '0px', 'padding-right': '5px' }}
                    >
                      <IconButton
                        dataTestId="delete-adjustment-button"
                        onClick={() => {
                          openDialogBox(DELETE_ADJUSTMENTS_LINE_ITEM_DIALOG_ID);
                          setDeleteIndex(index);
                        }}
                        disabled={orderStore.isReadOnly}
                      >
                        <DeleteIcon class={classes.deleteIcon} size={20} />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                  <TableRowErrors
                    columnsLength={columns.length}
                    tableErrors={orderTableErrors(
                      ['rate', 'quantity', 'type'],
                      'adjustments[{index}].{field}',
                      index,
                    )}
                  />
                </Show>
              )}
            </Index>
          }
          tableBodyClasses={classes.tableBg}
          tableFooterClasses={classes.tableFooter}
          tableBodyRowClasses={classes.tableFooterRow}
          tableFooterRowClasses={classes.tableFooterRow}
        />
      </Grid>
      <Grid item size={12} container class="mt-4">
        <Grid size={6}>
          <Checkbox
            label="Hold Retraction"
            checked={orderStore.order.holdRetraction}
            onChange={(value) => updateOrderState({ holdRetraction: value })}
            disabled={!isSuperAdmin()}
            dataTestId={'hold-retraction-checkboxInput'}
            size="xs"
            color="info"
            class="ml-[-9px]"
          />
        </Grid>
        <Grid size={6}>
          <DatePicker
            label="Revisit Retraction Hold Date"
            value={orderStore.order.revisitRetractionDate ?? ''}
            handleChange={async (value) => {
              updateOrderState({ revisitRetractionDate: value });
              await validateFieldForOrder(
                'revisitRetractionDate',
                'revisitRetractionDate',
              );
            }}
            error={orderStore.orderFormError?.revisitRetractionDate}
            disabled={!isSuperAdmin()}
            dataTestId={'revisit-retraction-hold-date-datepicker'}
          />
        </Grid>
      </Grid>
      <Grid size={12} class="mt-4">
        <TextField
          value={orderStore.order.exceptionReason}
          label={'Exception Reason'}
          maxLength={500}
          onChange={async (value: string) => {
            updateOrderState({ exceptionReason: value });
            await validateFieldForOrder('exceptionReason', 'exceptionReason');
          }}
          multiline
          placeholder="This is a note"
          error={
            orderStore.orderFormError &&
            orderStore.orderFormError['exceptionReason']
          }
          disabled={!isSuperAdmin()}
          size="sm"
          dataTestId={'exception-reason-textareafield'}
        />
      </Grid>
      <ConfirmationDialogBox
        isDialogOpen={checkIfDialogBoxIsOpen(
          DELETE_ADJUSTMENTS_LINE_ITEM_DIALOG_ID,
        )}
        dataTestId={DELETE_ADJUSTMENTS_LINE_ITEM_DIALOG_ID}
        id={DELETE_ADJUSTMENTS_LINE_ITEM_DIALOG_ID}
        title={'Are you sure you want to delete this adjustment?'}
        onSubmitText="Delete"
        onSubmit={() => {
          removeTableRow(
            deleteIndex()!,
            orderStore.order,
            'order',
            'adjustments',
          );
          setDeleteIndex(null);
        }}
        closeDialogBox={(_reason) => {
          setDeleteIndex(null);
          closeDialogBox(DELETE_ADJUSTMENTS_LINE_ITEM_DIALOG_ID);
        }}
      />
    </Grid>
  );
};
